import { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  Menu,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LaunchIcon from "@mui/icons-material/Launch";
import { useAppDispatch } from "../../app/hooks";
import { useNavigate } from "react-router-dom";
import {
  BackgroundProcessSummaryDto,
  loadMyBackgroundProcessesSummary,
  MyBackgroundProcessesSummaryDto,
} from "./backgroundProcessingApi";
import { BackgroundProcessSummaryDialog } from "./BackgroundProcessSummaryPanel";
import { getServerProcessRelatedDocumentLink } from "../documents/components/DocumentLinkProvider";
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import { statusPaletteColor as getStatusColor } from "./backgroundProcessStatus";

export const BackgroundProcessesButton = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<MyBackgroundProcessesSummaryDto>({
    activeCount: 0,
    completedTodayCount: 0,
    active: [],
    recentCompleted: [],
  });
  const [selectedProcessId, setSelectedProcessId] = useState<string | null>(null);

  const refreshSummary = async () => {
    setLoading(true);
    try {
      const result = await dispatch(loadMyBackgroundProcessesSummary());

      if (loadMyBackgroundProcessesSummary.fulfilled.match(result)) {
        setSummary(result.payload);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refreshSummary();

    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => {
        void refreshSummary();
      }, 30000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const count = summary.activeCount;

  const activeProcesses = useMemo(() => summary.active, [summary.active]);
  const recentCompleted = useMemo(() => summary.recentCompleted, [summary.recentCompleted]);

  const renderProcessItem = (process: BackgroundProcessSummaryDto, accent: "active" | "completed") => {
    const isActive = accent === "active";
    const documentLink = getServerProcessRelatedDocumentLink(process.relatedObjectType, process.relatedObjectId);

    return (
      <ListItem
        key={process.id}
        disablePadding
        sx={{
          border: "1px solid",
          borderColor: isActive ? "primary.100" : "divider",
          borderRadius: 2,
          backgroundColor: isActive ? "primary.50" : "background.paper",
          mb: 1,
        }}
      >
        <Box sx={{ width: "100%", px: 1.5, py: 1.25 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                #{process.processNumber} {process.operationType}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {process.displayName || `${process.relatedObjectType} #${process.relatedObjectId}`}
              </Typography>
              {isActive ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Started: {new Date(process.startedAt).toLocaleString()}
                </Typography>
              ) : (
                process.completedAt && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Completed: {new Date(process.completedAt).toLocaleString()}
                  </Typography>
                )
              )}
              {documentLink && (
                <Button
                  size="small"
                  variant="text"
                  startIcon={<LaunchIcon fontSize="small" />}
                  onClick={() => {
                    navigate(documentLink);
                    setAnchorEl(null);
                  }}
                  sx={{ mt: 0.5, px: 0, minWidth: 0, justifyContent: "flex-start" }}
                >
                  Open document
                </Button>
              )}
            </Box>
            <Stack spacing={0.75} alignItems="flex-end" sx={{ flexShrink: 0 }}>
              <Chip label={process.status} color={getStatusColor(process.status)} size="small" />
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedProcessId(process.id)}
                sx={{ minWidth: 72, px: 1, py: 0.25, fontSize: "0.72rem", lineHeight: 1.2 }}
              >
                Summary
              </Button>
            </Stack>
          </Stack>

        </Box>
      </ListItem>
    );
  };

  return (
    <Box>
      <Tooltip title="Background processes">
        <IconButton color="inherit" onClick={(event) => setAnchorEl(event.currentTarget)}>
          <Badge badgeContent={count} color="warning">
            <ManageHistoryIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: { sx: { width: 500, maxHeight: 640, borderRadius: 4, overflow: "hidden" } },
        }}
      >
        <Box
          px={2}
          pt={1.75}
          pb={1.25}
          sx={{
            background: "linear-gradient(135deg, rgba(2,136,209,0.08), rgba(46,125,50,0.08))",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Box>
              <Typography variant="subtitle1" fontWeight={800}>
                My background processes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active now and recently completed jobs
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Chip
                icon={<PlayCircleOutlineIcon />}
                label={`${summary.activeCount} active`}
                color="primary"
                size="small"
              />
              <Chip
                icon={<CheckCircleOutlineIcon />}
                label={`${summary.completedTodayCount} today`}
                color="success"
                size="small"
              />
            </Stack>
          </Stack>
        </Box>
        <Box sx={{ px: 2, py: 1.5 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="overline" sx={{ fontWeight: 700, color: "primary.main" }}>
                Active
              </Typography>
              <List disablePadding sx={{ mt: 0.75 }}>
                {activeProcesses.length === 0 ? (
                  <Box py={2} textAlign="center">
                    <Typography variant="body2" color="text.secondary">
                      {loading ? "Loading active processes..." : "No active processes."}
                    </Typography>
                  </Box>
                ) : (
                  activeProcesses.map(process => renderProcessItem(process, "active"))
                )}
              </List>
            </Box>

            <Divider />

            <Box>
              <Typography variant="overline" sx={{ fontWeight: 700, color: "success.main" }}>
                Recent completed
              </Typography>
              <Box sx={{ mt: 0.75, maxHeight: 240, overflowY: "auto", pr: 0.5 }}>
                <List disablePadding>
                  {recentCompleted.length === 0 ? (
                    <Box py={2} textAlign="center">
                      <Typography variant="body2" color="text.secondary">
                        {loading ? "Loading completed processes..." : "No completed processes for today."}
                      </Typography>
                    </Box>
                  ) : (
                    recentCompleted.map(process => renderProcessItem(process, "completed"))
                  )}
                </List>
              </Box>
            </Box>

            <Divider />

            <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
              <Button size="small" variant="text" onClick={() => void refreshSummary()}>
                Refresh
              </Button>
            </Box>

            <Button variant="contained" fullWidth disabled>
              Go to full list
            </Button>
          </Stack>
        </Box>
      </Menu>
      <BackgroundProcessSummaryDialog
        open={selectedProcessId !== null}
        onClose={() => setSelectedProcessId(null)}
        processId={selectedProcessId ?? undefined}
      />
    </Box>
  );
};